import {
    Brain,
    Cog,
    Shield,
    FileCheck,
    Users,
    Database,
    Cloud,
    Code,
    Settings,
    Lock,
    BarChart3,
    Layers,
    RefreshCw,
    GitBranch,
    Server,
    Cpu,
    Globe,
    Target,
    AlertTriangle,
    Terminal,
    Box,
    Key,
    Smartphone,
    Layout,
    MessageSquare,
    FileText,
    CloudLightning,
    Container,
} from 'lucide-react';
import { ServiceData } from '../types/service';

export const servicesEn: Record<string, ServiceData> = {
    'ai-automation': {
        title: 'AI & Automation',
        subtitle: 'Automation designed to increase reach and impact – without compromising data protection, quality, or accountability.',
        heroImage: '/images/ai_automation_hero.png',
        contextDesc: 'In enterprise and regulated environments, the difference between a "demo" and "production" is critical: data quality, governance, monitoring, and clear responsibilities. We help you guide AI projects from idea to stable operation – compliant with the EU AI Act.',
        operationalReality: [
            'Data is scattered (M365, SharePoint, ERP/CRM, Email, Files), often without clear data ownership',
            'Security concerns block innovation: "Are we even allowed to upload this to the cloud?"',
            'Lack of AI literacy: Employees don\'t know how to use prompts safely and effectively',
            'EU AI Act compliance: Uncertainty about risk classification and documentation obligations',
        ],
        deliverables: [
            'AI Readiness Assessment & Data Inventory',
            'Secure AI Gateway (Azure OpenAI / Bedrock) with PII Filter',
            'RAG Implementation (Chat with your Data) on your own documents',
            'AI Governance Framework & EU AI Act Gap Analysis',
            'Prompt Engineering Workshops & Guidelines',
            'Implementation as controlled workflows (not just "Chat everywhere")',
            'Observability: Logging, Metrics, Feedback Loops, Human-in-the-Loop where necessary',
            'Operating Model: Responsibilities, Review Cadence, Change/Rollback Process',
        ],
        capabilities: [
            { icon: Brain, title: 'AI Agents & Chatbots', desc: 'Channel strategy, knowledge boundaries, source binding, access scopes' },
            { icon: BarChart3, title: 'Predictive Analytics', desc: 'Data pipeline, feature definition, drift checks, validation rules' },
            { icon: FileCheck, title: 'Document Processing', desc: 'Classification, extraction, validation, audit trail, versioning' },
            { icon: Cog, title: 'Integrations', desc: 'API/Webhook design, queueing, error handling, retries, rate limits' },
            { icon: Shield, title: 'Data Controls', desc: 'PII filter, masking, access controls, retention' },
            { icon: RefreshCw, title: 'Operating Model', desc: 'Responsibilities, review cadence, change/rollback process' },
        ],
        techStack: [
            { icon: Brain, name: 'OpenAI / Azure OpenAI' },
            { icon: MessageSquare, name: 'LangChain' },
            { icon: Database, name: 'Pinecone / Vector DB' },
            { icon: FileText, name: 'Azure Document Intel' },
            { icon: Cloud, name: 'Azure AI Services' },
            { icon: Code, name: 'Python' },
        ],
        timeline: [
            {
                phase: 'Use-Case & Data Readiness',
                weeks: '2–4 Weeks',
                items: ['Use-Case Backlog', 'Data Inventory', 'Risk/Control Concept', 'Success Criteria'],
            },
            {
                phase: 'Pilot',
                weeks: '4–8 Weeks',
                items: ['Pilot Workflow', 'Logging/Metrics', 'Validation Rules', 'Runbook'],
                validation: ['Test Datasets', 'Error Rates', 'Human Approvals', 'Access Tests'],
            },
            {
                phase: 'Scale & Operations',
                weeks: '6–12 Weeks',
                items: ['Governance', 'SLOs/Monitoring', 'Change/Rollback', 'Training', 'Ownership Handover'],
            },
        ],
        riskCompliance: [
            { category: 'Data Protection', items: ['Clear Data Sources', 'Purpose Limitation', 'Minimization', 'Access Scopes'] },
            { category: 'Quality', items: ['Validation Rules', 'Spot Checks', 'Drift/Anomaly Checks'] },
            { category: 'Evidence', items: ['Logs', 'Prompt/Policy Configurations', 'Approvals', 'Release Notes'] },
        ],
        enablement: [
            'Playbooks: Prompt/Policy Changes, Incident Handling, Data-Change Impact',
            'Training: Admin/Owner Training + Quick Guide for Departments',
        ],
        nextSteps: [
            { title: 'Use-Case Screening', duration: '60 Min.', desc: '3–5 Candidates + Risk Assessment' },
            { title: 'Pilot Assessment', duration: '2–4 Weeks', desc: 'Data/Access Concept + Pilot Plan' },
        ],
    },
    'erp-sap': {
        title: 'ERP & SAP',
        subtitle: 'Stabilization and further development of core ERP processes with a clear focus on integration, compliance, and operations.',
        heroImage: '/images/erp_sap_hero.png',
        contextDesc: 'ERP systems are the process and data core in many organizations. Changes must be stable, traceable, and integrated – especially when interfaces, compliance, and operational requirements are involved.',
        operationalReality: [
            'Legacy integrations, custom extensions, long release cycles',
            'Dependencies on logistics/finance/reporting, low tolerance for downtime',
            'Multiple environments (DEV/QA/PRD), hard cutover windows, tight testing time',
        ],
        deliverables: [
            'Stabilization and development of core ERP processes',
            'Interface and integration catalog (Ownership, SLA, Error Paths)',
            'Release and test strategy (Quality Gates, Regression, Acceptance)',
            'Integration into security/governance models (Access, Audit Trails, Changes)',
            'Documentation and handover to operations',
        ],
        architectureBlueprint: {
            title: 'Standard Architecture Blueprint: AI Agent ↔ SAP',
            description:
                'Reference architecture for secure AI integration into SAP backends, with privacy, governance, and audit trails built in.',
            image: '/images/service_abstract_geometric.png',
            bullets: [
                'Privacy proxy masks PII before agent calls',
                'SAP BTP/Integration Suite as controlled interface',
                'Audit logs and change control for transport paths',
            ],
        },
        capabilities: [
            { icon: Layers, title: 'SAP S/4HANA', desc: 'Process analysis, extensions, data quality, transport/release discipline' },
            { icon: Code, title: 'SAP Fiori Development', desc: 'Roles/Apps, UX constraints, permissions, deployment gates' },
            { icon: Cog, title: 'ERP Integration', desc: 'API/EDI contracts, mapping rules, error handling, monitoring' },
            { icon: FileCheck, title: 'Evidence', desc: 'Change logs, transport lists, approvals, interface protocols' },
            { icon: Database, title: 'Data Migration', desc: 'Data quality, mapping, validation, cutover planning' },
            { icon: Shield, title: 'Governance', desc: 'Authorization concept, audit trails, compliance evidence' },
        ],
        techStack: [
            { icon: Layers, name: 'SAP S/4HANA' },
            { icon: Layout, name: 'SAP Fiori' },
            { icon: Cloud, name: 'SAP BTP' },
            { icon: Cog, name: 'SAP Integration Suite' },
            { icon: Database, name: 'SAP HANA' },
            { icon: Code, name: 'ABAP' },
        ],
        timeline: [
            {
                phase: 'Assessment & Inventory',
                weeks: '2–4 Weeks',
                items: ['Process/Interface Inventory', 'Risk/Stability Analysis', 'Roadmap'],
            },
            {
                phase: 'Stabilize & Govern',
                weeks: '4–8 Weeks',
                items: ['Quality Gates', 'Test Strategy', 'Monitoring for Critical Flows', 'Role/Access Review'],
            },
            {
                phase: 'Implement & Release',
                weeks: 'Ongoing, iterative',
                items: ['Releases with Acceptance Protocol', 'Interface Contracts', 'Operations Runbooks'],
            },
        ],
        riskCompliance: [
            { category: 'Controlled Changes', items: ['Defined Approvals', 'Clear Rollback Criteria', 'Documented Acceptances'] },
            { category: 'Auditability', items: ['Traceable changes to Customizing/Code/Interfaces'] },
            { category: 'Data Protection', items: ['Purpose limitation and data minimization in interface contracts'] },
        ],
        enablement: [
            'Operations Handover: Runbooks, Interface Checklists, Escalation Paths',
            'Enablement: Training for Key Users/Admins, Release and Test Processes',
        ],
        nextSteps: [
            { title: 'ERP/Interface Inventory Workshop', duration: '90 Min.', desc: 'Inventory of your system landscape' },
            { title: 'Assessment', duration: '2–4 Weeks', desc: 'Inventory + Release/Governance Plan' },
        ],
    },
    'software-engineering': {
        title: 'Software Engineering',
        subtitle: 'Tailored software solutions using modern technologies and proven engineering practices.',
        heroImage: '/images/software_engineering_hero.png',
        contextDesc: 'Software is not an end in itself – it must solve business problems, be maintainable, and integrate into existing landscapes. We don\'t deliver isolated features, but thoughtful solutions with clear ownership and operability.',
        operationalReality: [
            'Existing systems with technical debt and unclear responsibilities',
            'Pressure for fast delivery combined with high quality requirements',
            'Fragmented toolchains and lack of observability',
        ],
        deliverables: [
            'Requirements Engineering and Architecture Design',
            'Development with modern tech stack (React, TypeScript, Node.js, Cloud)',
            'CI/CD pipelines and automated tests',
            'API design and integration into existing systems',
            'Documentation, code reviews, and knowledge transfer',
        ],
        capabilities: [
            { icon: Code, title: 'Frontend Development', desc: 'React, Vue, Angular with TypeScript and modern UI frameworks' },
            { icon: Server, title: 'Backend Development', desc: 'Node.js, Python, Go with RESTful APIs and GraphQL' },
            { icon: Cloud, title: 'Cloud-Native', desc: 'Kubernetes, Serverless, Microservices Architecture' },
            { icon: Database, title: 'Data Engineering', desc: 'SQL, NoSQL, Data Pipelines, and ETL' },
            { icon: GitBranch, title: 'DevOps', desc: 'CI/CD, Infrastructure as Code, Container Orchestration' },
            { icon: Shield, title: 'Security', desc: 'SAST, DAST, Dependency Scanning, Security Reviews' },
        ],
        techStack: [
            { icon: Code, name: 'React / TypeScript' },
            { icon: Server, name: 'Node.js / NestJS' },
            { icon: Box, name: 'Docker' },
            { icon: Cloud, name: 'AWS / Azure' },
            { icon: Database, name: 'PostgreSQL' },
            { icon: Terminal, name: 'Terraform' },
        ],
        timeline: [
            {
                phase: 'Discovery & Architecture',
                weeks: '2–4 Weeks',
                items: ['Requirements', 'System Design', 'Tech Stack Selection', 'Roadmap'],
            },
            {
                phase: 'Development Sprints',
                weeks: '8–16 Weeks',
                items: ['MVP', 'Iterative Releases', 'CI/CD Pipeline', 'Code Reviews'],
                validation: ['Automated Tests', 'Manual QA', 'Security Scans', 'Performance Tests'],
            },
            {
                phase: 'Launch & Optimization',
                weeks: '2–4 Weeks',
                items: ['Production Deployment', 'Monitoring', 'Performance Tuning', 'Handover'],
            },
        ],
        riskCompliance: [
            { category: 'Code Quality', items: ['Linting', 'Code Reviews', 'Test Coverage', 'Technical Debt Management'] },
            { category: 'Security', items: ['OWASP Checks', 'Dependency Audits', 'Secret Management'] },
            { category: 'Compliance', items: ['License Compliance', 'Accessibility', 'GDPR-compliant Data Processing'] },
        ],
        enablement: [
            'Technical Documentation and Architecture Decision Records (ADRs)',
            'Pair Programming and Knowledge Sharing Sessions',
            'Runbooks for Deployment and Incident Handling',
        ],
        nextSteps: [
            { title: 'Discovery Workshop', duration: '2–4 Hours', desc: 'Requirements and Architecture Scoping' },
            { title: 'MVP Sprint', duration: '4–6 Weeks', desc: 'Proof of Concept with real value' },
        ],
    },
    'cloud-platform-foundations': {
        title: 'Cloud & Platform Foundations',
        subtitle: 'Secure, scalable cloud infrastructure as the foundation for your digital transformation.',
        heroImage: '/images/cloud_platform_hero.png',
        contextDesc: 'Cloud is more than infrastructure – it is an operating model. We help you build Landing Zones, migrate workloads, and empower teams to work independently and securely in the cloud.',
        operationalReality: [
            'On-premises legacy with unclearly documented dependencies',
            'Compliance requirements and data residency mandates',
            'Teams with varying levels of cloud maturity',
        ],
        deliverables: [
            'Cloud Readiness Assessment and Migration Strategy',
            'Landing Zone with Security Baseline and Governance',
            'Infrastructure as Code Templates (Terraform, CloudFormation)',
            'Workload Migration with Validation and Rollback Planning',
            'Team Enablement and Self-Service Empowerment',
        ],
        architectureBlueprint: {
            title: 'Standard Architecture Blueprint: Secure AI Platform',
            description:
                'Blueprint for an auditable AI platform in the cloud with clear data and security boundaries.',
            image: '/images/service_abstract_geometric.png',
            bullets: [
                'Identity & policy layer for controlled access',
                'Observability with security and audit logging',
                'Data classification, residency, and encryption by default',
            ],
        },
        capabilities: [
            { icon: Cloud, title: 'Cloud Strategy', desc: 'Assessment, Roadmap, and Business Case for Cloud Migration' },
            { icon: Server, title: 'Landing Zone', desc: 'Secure, scalable foundation for your cloud workloads' },
            { icon: Lock, title: 'Identity & Access', desc: 'IAM, SSO, MFA, Privileged Access Management' },
            { icon: Globe, title: 'Networking', desc: 'VPC, Peering, Transit Gateway, CDN, DNS' },
            { icon: Shield, title: 'Security & Compliance', desc: 'Security Hub, Config Rules, Audit Logging' },
            { icon: Cpu, title: 'Container & Kubernetes', desc: 'EKS, AKS, GKE – Orchestration for modern workloads' },
        ],
        techStack: [
            { icon: Cloud, name: 'AWS' },
            { icon: CloudLightning, name: 'Azure' },
            { icon: Terminal, name: 'Terraform' },
            { icon: Container, name: 'Kubernetes' },
            { icon: GitBranch, name: 'GitHub Actions' },
            { icon: Shield, name: 'HashiCorp Vault' },
        ],
        timeline: [
            {
                phase: 'Assessment & Strategy',
                weeks: '2–3 Weeks',
                items: ['Cloud Readiness', 'Migration Plan', 'Cost Analysis', 'Risk Assessment'],
            },
            {
                phase: 'Infrastructure Setup',
                weeks: '4–8 Weeks',
                items: ['IaC Templates', 'Security Baseline', 'Networking', 'Monitoring'],
            },
            {
                phase: 'Migration & Enablement',
                weeks: '4–12 Weeks',
                items: ['Workload Migration', 'Team Training', 'Operations Handover', 'Documentation'],
                validation: ['Functional Tests', 'Performance Benchmarks', 'Security Scans'],
            },
        ],
        riskCompliance: [
            { category: 'Security', items: ['Network Segmentation', 'Encryption at Rest/Transit', 'WAF/DDoS Protection'] },
            { category: 'Compliance', items: ['Data Residency', 'Audit Logging', 'Retention Policies'] },
            { category: 'Cost Management', items: ['Budgets & Alerts', 'Reserved Instances', 'Right-Sizing'] },
        ],
        enablement: [
            'Cloud-native Training for Developers and Ops Teams',
            'Self-Service Playbooks for common scenarios',
            'FinOps Awareness and Cost Optimization Workshops',
        ],
        nextSteps: [
            { title: 'Cloud Readiness Assessment', duration: '1–2 Weeks', desc: 'Inventory and Migration Candidates' },
            { title: 'Landing Zone Pilot', duration: '4–6 Weeks', desc: 'First workload in secure environment' },
        ],
    },
    'change-management-delivery': {
        title: 'Change Management & Delivery',
        subtitle: 'Ensuring business continuity and minimizing risk in complex IT carve-outs and transformations.',
        heroImage: '/images/change_management_hero.png',
        contextDesc: 'Disentangling legacy IT structures poses a significant operational risk. Without stringent management, system failures and process interruptions threaten business operations. We view Change Management as Risk Management: Through clear governance, precise interface analysis, and structured migration plans, we ensure Business Continuity. Our goal is the transition to an autonomous, future-proof IT landscape with minimal disruption to daily business. Structured change significantly reduces rework, productivity losses, and operational disruptions, and is a key factor in meeting time and budget goals.',
        realityTag: 'Operational Impact',
        riskTag: 'Risk Reduction',
        benefitsTag: 'Business Value',
        operationalReality: [
            'Small internal IT department with limited resources for complex carve-out projects',
            'Interwoven IT systems with unclear dependencies and interfaces to the parent company',
            'High time and budget pressure combined with the need for operational stability',
            'Outdated processes require modernization and realignment of the IT landscape',
        ],
        deliverables: [
            'IT Autonomy: Fully independent infrastructure and identity management',
            'Operational Stability: Secure, scalable operations after carve-out completion',
            'Digitalization as Opportunity: Using transformation to modernize legacy systems',
            'Cloud-native architectures and automated processes',
            'Defined Service Levels and documented processes',
        ],
        capabilities: [
            { icon: Users, title: 'Steering Committee', desc: 'Monthly meetings, strategic decisions, budget approvals, escalation instance' },
            { icon: Target, title: 'Project Management', desc: 'Weekly status meetings, coordination of all workstreams, risk management' },
            { icon: Settings, title: 'Technical Teams', desc: 'Dedicated teams for infrastructure, applications, security. Daily stand-ups' },
            { icon: MessageSquare, title: 'Communication', desc: 'Weekly status reports, executive summaries, dedicated project portal' },
            { icon: Shield, title: 'Risk Management', desc: 'Structured risk management per PMI standards, weekly reviews' },
            { icon: FileCheck, title: 'Quality Assurance', desc: 'ISO 9001-certified processes, four-eyes principle, automated tests' },
        ],
        techStack: [
            {
                icon: Users,
                name: 'Miro / Mural',
                desc: 'Structured participation & shared decision-making. Miro supports active stakeholder involvement in all transformation phases.',
                benefits: [
                    'Shared understanding of target state, risks, and impacts',
                    'Structured workshops instead of uncontrolled discussions',
                    'Traceable decision basis for Management & PMO',
                ],
            },
            {
                icon: Layout,
                name: 'Jira / Confluence',
                desc: 'Transparency on progress, risks, and dependencies. Jira enables fact-based control of change measures.',
                benefits: [
                    'Clear traceability of change measures',
                    'Early identification of bottlenecks and escalations',
                    'Reliable basis for status reports and steering committees',
                ],
            },
            {
                icon: MessageSquare,
                name: 'Microsoft Teams',
                desc: 'Reliable communication during transformation. Teams is the central channel for structured, consistent, and targeted information.',
                benefits: [
                    'Reduction of rumors and information gaps',
                    'Clear communication formats for leaders and employees',
                    'Direct accessibility during critical project phases',
                ],
            },
            {
                icon: FileText,
                name: 'SharePoint',
                desc: 'Binding information source & knowledge base. SharePoint serves as the Single Source of Truth for all change-relevant information.',
                benefits: [
                    'Clear orientation for all participants',
                    'Avoidance of information loss',
                    'Sustainable handover to operational business',
                ],
            },
            {
                icon: BarChart3,
                name: 'Power BI',
                desc: 'Controllability through measurable change. Power BI translates change into manageable KPIs.',
                benefits: [
                    'Objective decision basis for management',
                    'Transparency on project progress and risks',
                    'Early warning system for deviations from time or targets',
                ],
            },
        ],
        timeline: [
            {
                phase: 'Baselining & IT Strategy',
                weeks: '6–8 Weeks',
                items: ['Inventory', 'Dependency Analysis', 'Target Architecture', 'Roadmap Development'],
            },
            {
                phase: 'Infrastructure Setup',
                weeks: '8–12 Weeks',
                items: ['New Tenant Setup', 'Cloud Environments', 'Network', 'Identity & Access Management'],
            },
            {
                phase: 'Migration & Separation',
                weeks: '12–16 Weeks',
                items: ['Data Migration', 'Application Move', 'System Separation', 'Interface Adaptation'],
            },
            {
                phase: 'Testing & Stabilization',
                weeks: '6–8 Weeks',
                items: ['Integration Tests', 'Security Checks', 'Performance Optimization', 'Documentation'],
            },
            {
                phase: 'Go-Live & Hypercare',
                weeks: '4–6 Weeks',
                items: ['Go-Live', 'Intensive Monitoring', 'Incident Management', 'Knowledge Transfer'],
            },
        ],
        riskCompliance: [
            { category: 'Risk Minimization', items: ['Structured Risk Management (PMI)', 'Weekly Reviews', 'Defined Escalation Paths'] },
            { category: 'Quality Assurance', items: ['ISO 9001 Processes', 'Four-Eyes Principle', 'Acceptance Criteria for Milestones'] },
            { category: 'Security & Compliance', items: ['ISO 27001 Compliant', 'GDPR Compliance', 'Encryption of Sensitive Data', 'Audit Trails'] },
        ],
        enablement: [
            'Training for IT staff and handover of documentation/runbooks',
            'Shadowing and hands-on training for the internal IT team',
            'Establishment of support processes and retained support agreements',
        ],
        detailedEnablement: [
            {
                title: 'Structured Knowledge Transfer',
                desc: 'Knowledge is not implicitly assumed but built up specifically and traceably. All relevant technical, organizational, and operational content is systematically handed over to the internal IT team.',
                items: [
                    'Handover of complete system and operations documentation',
                    'Runbooks, emergency, and escalation processes',
                    'Architecture and decision documentation',
                ],
            },
            {
                title: 'Empowerment of Internal IT Organization',
                desc: 'Through practical training and accompanying collaboration, the internal team is empowered to operate systems securely, develop them further, and implement future changes independently.',
                items: [
                    'Role-based training for IT, operations, and departments',
                    'Shadowing and hands-on training during the project',
                    'Clear responsibilities for operations and development',
                ],
            },
            {
                title: 'Transition to Stable Operations',
                desc: 'The handover is not punctual but structured and secured. This ensures that operational business can continue stably, transparently, and scalably after project completion.',
                items: [
                    'Defined handover milestones',
                    'Acceptance by departments and IT',
                    'Established support and operations processes',
                ],
            },
            {
                title: 'Optional Support without Dependency',
                desc: 'If needed, we accompany the transition with time-limited retained support. The goal is not permanent binding, but controlled stabilization and gradual withdrawal.',
                items: [
                    'Clearly time-limited support agreements',
                    'Knowledge transfer instead of ticket processing',
                    'Planned exit of external support',
                ],
            },
        ],
        benefits: [
            { title: 'Executive Management', desc: 'Maximum risk minimization in carve-outs, guaranteed Business Continuity, and audit-proof compliance (ISO/GDPR).' },
            { title: 'Line Managers', desc: 'Operational stability during transition, clear escalation paths, and relieved teams through structured Hypercare.' },
            { title: 'Employees', desc: 'Planning security through transparent communication, targeted enablement, and frustration-free adoption of new systems.' },
        ],
        nextSteps: [
            { title: 'Baselining Workshop', duration: '1 Day', desc: 'Initial inventory and scope definition' },
            { title: 'IT Strategy Assessment', duration: '2–4 Weeks', desc: 'Detailed analysis and roadmap development' },
        ],
    },
    'strategy-and-transformation': {
        title: 'Strategy & Transformation',
        subtitle: 'Build a measurable transformation roadmap with clear governance, investment priorities, and an execution plan that teams can deliver.',
        heroImage: '/images/change_management_hero.png',
        contextDesc: 'Modern transformations fail less because of technology—and more because priorities, governance, and execution discipline aren’t aligned. We help leadership teams establish a clear direction, create cost transparency across portfolios and vendors, and translate strategy into an executable plan with governance, KPIs, and a delivery operating model. The result is faster decision-making, reduced delivery risk, and measurable business outcomes.',
        realityTag: 'Operational Impact',
        riskTag: 'Risk Reduction',
        benefitsTag: 'Business Value',
        operationalReality: [
            'Too many initiatives, unclear priorities, and "everything is urgent"',
            'Limited cost transparency across apps, vendors, and licenses',
            'Weak governance: slow decisions, unclear ownership, fragmented reporting',
            'Change fatigue: low adoption, resistance, and benefits not realized',
        ],
        deliverables: [
            'Enterprise Roadmap (12–18 months): Prioritization, sequencing, milestones, dependencies, and value targets.',
            'Portfolio & Cost Baseline: Transparency across apps, vendors, and licensing.',
            'Operating Model & Governance: Clear decision rights and KPI ownership.',
            'Transformation PMO Setup: Delivery plans, RAID logs, and reporting automation.',
            'Change Enablement: Stakeholder mapping and adoption metrics.',
        ],
        capabilities: [
            { icon: Users, title: 'Steering Committee', desc: 'Decision cadence, approvals, escalation, outcome tracking.' },
            { icon: Target, title: 'Program Management', desc: 'Workstreams, milestones, dependencies, risk management.' },
            { icon: Settings, title: 'Architecture & Delivery Teams', desc: 'Target architecture, delivery standards, quality gates.' },
            { icon: MessageSquare, title: 'Communication & Alignment', desc: 'Updates, decision logs, stakeholder messaging, transparency.' },
            { icon: Shield, title: 'Risk Management', desc: 'RAID discipline, mitigation plans, issue resolution.' },
            { icon: FileCheck, title: 'Quality Assurance', desc: 'Acceptance criteria, governance gates, documentation quality.' },
        ],
        capabilitiesTitle: 'Technical Building Blocks',
        deliverablesTitle: 'Our Services',
        techStack: [
            {
                icon: Users,
                name: 'Miro / Mural',
                desc: 'Structured participation & shared decision-making. Miro supports active stakeholder involvement in all transformation phases.',
                benefits: [
                    'Shared understanding of target state, risks, and impacts',
                    'Structured workshops instead of uncontrolled discussions',
                    'Traceable decision basis for Management & PMO',
                ],
            },
            {
                icon: Layout,
                name: 'Jira / Confluence',
                desc: 'Transparency on progress, risks, and dependencies. Jira enables fact-based control of change measures.',
                benefits: [
                    'Clear traceability of change measures',
                    'Early identification of bottlenecks and escalations',
                    'Reliable basis for status reports and steering committees',
                ],
            },
            {
                icon: MessageSquare,
                name: 'Microsoft Teams',
                desc: 'Reliable communication during transformation. Teams is the central channel for structured, consistent, and targeted information.',
                benefits: [
                    'Reduction of rumors and information gaps',
                    'Clear communication formats for leaders and employees',
                    'Direct accessibility during critical project phases',
                ],
            },
            {
                icon: FileText,
                name: 'SharePoint',
                desc: 'Binding information source & knowledge base. SharePoint serves as the Single Source of Truth for all change-relevant information.',
                benefits: [
                    'Clear orientation for all participants',
                    'Avoidance of information loss',
                    'Sustainable handover to operational business',
                ],
            },
            {
                icon: BarChart3,
                name: 'Power BI',
                desc: 'Controllability through measurable change. Power BI translates change into manageable KPIs.',
                benefits: [
                    'Objective decision basis for management',
                    'Transparency on project progress and risks',
                    'Early warning system for deviations from time or targets',
                ],
            },
        ],
        timeline: [
            {
                phase: 'Baseline & Strategic Alignment',
                weeks: '2–4 weeks',
                items: ['Portfolio inventory', 'Current-state assessment', 'Transformation charter', 'Value hypotheses', 'RAID log'],
            },
            {
                phase: 'Target Operating Model & Governance',
                weeks: '2–4 weeks',
                items: ['TOM options', 'RACI', 'Governance playbook', 'KPI/OKR framework', 'Reporting templates'],
            },
            {
                phase: 'Roadmap & Investment Plan',
                weeks: '3–6 weeks',
                items: ['12–18 month roadmap', 'Sequencing & dependencies', 'Investment ranges', 'Resourcing plan', 'Vendor plan'],
            },
            {
                phase: 'Program Mobilization',
                weeks: '2–4 weeks',
                items: ['Program plan', 'Workstreams', 'Delivery cadence', 'Steering pack', 'Dashboards', 'Acceptance criteria'],
            },
            {
                phase: 'Enablement & Adoption',
                weeks: 'ongoing / 4–8 weeks',
                items: ['Stakeholder map', 'Comms plan', 'Training approach', 'Adoption KPIs', 'Change champions model'],
            },
        ],
        riskCompliance: [
            { category: 'Risk Minimization', items: ['RAID discipline', 'escalation paths', 'weekly reviews', 'decision logs'] },
            { category: 'Quality Assurance', items: ['Acceptance criteria', 'quality gates', 'documentation standards'] },
            { category: 'Security & Privacy', items: ['GDPR-aware process', 'audit-ready documentation', 'access controls for program assets'] },
        ],
        enablement: [
            'Training for IT staff and handover of documentation/runbooks',
            'Shadowing and hands-on training for the internal IT team',
            'Establishment of support processes and retained support agreements',
        ],
        detailedEnablement: [
            {
                title: 'Structured Knowledge Transfer',
                desc: 'Knowledge is not implicitly assumed but built up specifically and traceably. All relevant technical, organizational, and operational content is systematically handed over to the internal IT team.',
                items: [
                    'Handover of complete system and operations documentation',
                    'Runbooks, emergency, and escalation processes',
                    'Architecture and decision documentation',
                ],
            },
            {
                title: 'Empowerment of Internal IT Organization',
                desc: 'Through practical training and accompanying collaboration, the internal team is empowered to operate systems securely, develop them further, and implement future changes independently.',
                items: [
                    'Role-based training for IT, operations, and departments',
                    'Shadowing and hands-on training during the project',
                    'Clear responsibilities for operations and development',
                ],
            },
            {
                title: 'Transition to Stable Operations',
                desc: 'The handover is not punctual but structured and secured. This ensures that operational business can continue stably, transparently, and scalably after project completion.',
                items: [
                    'Defined handover milestones',
                    'Acceptance by departments and IT',
                    'Established support and operations processes',
                ],
            },
            {
                title: 'Optional Support without Dependency',
                desc: 'If needed, we accompany the transition with time-limited retained support. The goal is not permanent binding, but controlled stabilization and gradual withdrawal.',
                items: [
                    'Clearly time-limited support agreements',
                    'Knowledge transfer instead of ticket processing',
                    'Planned exit of external support',
                ],
            },
        ],
        benefits: [
            { title: 'Executive Management', desc: 'Clear priorities, investment plan, and governance—with executive-ready reporting and decision packs.' },
            { title: 'Transformation Leaders / PMO', desc: 'A structured program plan, RAID management, dependencies, and delivery cadence that teams can actually run.' },
            { title: 'Business & IT Leaders', desc: 'Aligned operating model, defined KPIs/OKRs, and cross-functional execution with minimal friction.' },
        ],
        nextSteps: [
            { title: 'Baselining Workshop', duration: '1 Day', desc: 'Initial inventory and scope definition' },
            { title: 'IT Strategy Assessment', duration: '2–4 Weeks', desc: 'Detailed analysis and roadmap development' },
        ],
    },
    'security-privacy-governance': {
        title: 'Security, Privacy & Governance',
        subtitle: 'Security, data protection, and compliance as an integral part of your digital processes.',
        heroImage: '/images/security_governance_hero.png',
        contextDesc: 'Security is not a one-time project, but a continuous process. For enterprises and public institutions, customer and partner trust is a core asset. We establish pragmatic ISMS structures that protect you without paralyzing delivery – and make you fit for NIS2.',
        operationalReality: [
            'Rising cyber threats (Ransomware, Phishing) targeting critical operations and supply chains',
            'Limited budgets and personnel for dedicated security teams',
            'Complex compliance requirements (GDPR, Donation Seals, NIS2) with little expertise',
        ],
        deliverables: [
            'Security Assessment & Roadmap (NIS2 Readiness Check)',
            'Virtual CISO (vCISO) Service',
            'Identity & Access Management (IAM) Modernization',
            'Cloud Security Posture Management (CSPM)',
            'Awareness Campaigns & Phishing Simulations',
        ],
        capabilities: [
            { icon: Lock, title: 'Identity & Access', desc: 'MFA, Conditional Access, Privileged Access Management' },
            { icon: Shield, title: 'Endpoint Security', desc: 'EDR, MDM, Device Compliance, and Patch Management' },
            { icon: FileCheck, title: 'Data Protection', desc: 'DLP, Classification, Encryption, and Backup' },
            { icon: AlertTriangle, title: 'Incident Response', desc: 'Detection, Playbooks, and Coordinated Response' },
            { icon: BarChart3, title: 'Risk Management', desc: 'Risk Assessment, Treatment, and Monitoring' },
            { icon: Settings, title: 'Compliance', desc: 'ISO 27001, GDPR, SOC 2, NIS2, and industry-specific standards' },
        ],
        techStack: [
            { icon: Key, name: 'Azure AD / Entra ID' },
            { icon: Shield, name: 'Microsoft Sentinel' },
            { icon: Lock, name: 'CrowdStrike' },
            { icon: Smartphone, name: 'Intune MDM' },
            { icon: FileCheck, name: 'OneTrust' },
            { icon: AlertTriangle, name: 'Splunk' },
        ],
        timeline: [
            {
                phase: 'Security Assessment',
                weeks: '2-4 Weeks',
                items: ['Risk Register', 'Vulnerability Report', 'NIS2 Gap Analysis', 'Roadmap'],
                validation: ['Management Presentation of Risks'],
            },
            {
                phase: 'Foundation & Quick Wins',
                weeks: '4-8 Weeks',
                items: ['MFA Rollout', 'Endpoint Protection', 'Backup Review', 'Incident Response Plan'],
                validation: ['Security Score Improvement'],
            },
            {
                phase: 'ISMS & Governance',
                weeks: '3-6 Months',
                items: ['Policy Creation', 'Process Definition', 'Employee Training', 'Audit Preparation'],
                validation: ['Internal Audit / Certification'],
            },
        ],
        riskCompliance: [
            { category: 'Regulatory', items: ['GDPR', 'ISO 27001', 'BSI IT-Grundschutz', 'NIS2'] },
            { category: 'Operational', items: ['Business Continuity', 'Disaster Recovery', 'Supply Chain Security'] },
        ],
        enablement: [
            'Security Champions Program',
            'Regular Phishing Simulations',
            'Tabletop Exercises for Management',
        ],
        nextSteps: [
            { title: 'Schedule Security Quick-Check', duration: '2 Days', desc: 'Gap Analysis and Top Risks' },
            { title: 'NIS2 Impact Analysis', duration: '1 Week', desc: 'Prioritization and Action Planning' },
        ],
    },
    'sustainability-esg-tech': {
        title: 'Sustainability & ESG Tech',
        subtitle: 'Turn CSRD obligations into a data advantage. Automated ESG reporting instead of Excel chaos.',
        heroImage: '/images/sustainability_esg_hero.png',
        contextDesc: 'The Corporate Sustainability Reporting Directive (CSRD) forces companies to report sustainability data as rigorously as financial data. Manual processes no longer scale here. We build the data infrastructure for your sustainability goals.',
        operationalReality: [
            'Data Chaos: ESG data lies in silos (electricity bills, travel expenses, production)',
            'Manual Error Prone: Excel-based CO2 calculations are not audit-proof',
            'Increasing Pressure: Banks and customers demand reliable ESG evidence',
            'Missing Integration: Sustainability is not integrated into core processes (ERP)',
        ],
        deliverables: [
            'ESG Data Strategy & Architecture',
            'Automated Carbon Footprint Calculation (Scope 1, 2, 3)',
            'CSRD Reporting Platform Implementation',
            'Supply Chain Transparency (LkSG)',
            'Green IT & Cloud Optimization',
        ],
        capabilities: [
            { icon: FileCheck, title: 'Data Collection', desc: 'Automated connectors to ERP, Facility Management, and Travel Expense Systems.' },
            { icon: BarChart3, title: 'Calculation Engine', desc: 'Audit-proof calculation of emissions according to GHG Protocol.' },
            { icon: Layout, title: 'Reporting Hub', desc: 'Central platform for CSRD, EU Taxonomy, and LkSG reports.' },
            { icon: Users, title: 'Supplier Portal', desc: 'Digital collection of supplier data for Scope 3.' },
            { icon: FileCheck, title: 'Audit Trail', desc: 'Seamless traceability of every data change for the auditor.' },
            { icon: BarChart3, title: 'Dashboarding', desc: 'Real-time KPIs for management to steer sustainability goals.' },
        ],
        techStack: [
            { icon: Cloud, name: 'Microsoft Sustainability Manager' },
            { icon: Database, name: 'SAP Sustainability Control Tower' },
            { icon: BarChart3, name: 'Power BI' },
            { icon: Code, name: 'Python / Pandas' },
            { icon: Server, name: 'Azure Data Lake' },
            { icon: FileText, name: 'Workiva' },
        ],
        timeline: [
            {
                phase: 'Discovery & Materiality',
                weeks: '4-6 Weeks',
                items: ['Materiality Analysis', 'Data Source Inventory', 'Architecture Design'],
                validation: ['Scope Definition'],
            },
            {
                phase: 'Data Foundation',
                weeks: '8-12 Weeks',
                items: ['Data Lake Setup', 'Connector Implementation', 'Data Cleansing'],
                validation: ['Data Quality Check'],
            },
            {
                phase: 'Reporting & Dashboarding',
                weeks: '6-8 Weeks',
                items: ['CSRD Report Layout', 'Management Dashboards', 'Audit Preparation'],
                validation: ['Test Report'],
            },
        ],
        riskCompliance: [
            { category: 'Regulatory', items: ['CSRD', 'EU Taxonomy', 'LkSG', 'GHG Protocol'] },
            { category: 'Data', items: ['Data Integrity', 'Auditability', 'Third-Party Data Risk'] },
        ],
        enablement: [
            'Training departments on data entry',
            'Handover to Sustainability Office',
            'Regular Data Quality Reviews',
        ],
        nextSteps: [
            { title: 'ESG Data Readiness Check', duration: '1 Week', desc: 'Inventory and Migration Candidates' },
            { title: 'CSRD Gap Analysis', duration: '2-3 Weeks', desc: 'Prioritization and Action Planning' },
        ],
    },
};
